import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import {
  Calendar,
  Trophy,
  MessageCircle,
  Users,
  MapPin,
  Instagram,
  Music2,
  type LucideIconData,
} from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import type { GameCommunityContent, UpcomingEvent } from '../../models/game-section.model';
import { GAME_SECTION_COPY, interpolateGameCopy } from '../../constants/game-section.constants';
import {
  eventTypeLabel,
  formatEventPrice,
  formatEventRange,
} from '../../../events/utils/event-format.util';
import {
  EVENTS_COPY,
  UPCOMING_EVENTS_LIMITS,
} from '../../../events/constants/events.constants';
import { EventsService } from '../../../events/data/events.service';
import { EventDialogService } from '../../../events/data/event-dialog.service';
import type { StoreEvent } from '../../../events/data/events.model';

/**
 * Un evento ya clasificado para pintarse. `isFiller` gobierna **las dos**
 * señales a la vez —chip y acento— y por eso viaja junto al evento en vez de
 * recalcularse en cada binding: son la misma decisión, y separarlas es lo que
 * permitiría el caso mixto (acento de juego **con** chip) que D3 y D9 prohíben.
 */
interface CommunityEventView {
  event: UpcomingEvent;
  isFiller: boolean;
}

/** Milisegundos de inicio; una fecha corrupta va al final sin romper el orden. */
function startTime(event: UpcomingEvent): number {
  const parsed = Date.parse(event.startsAt);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

@Component({
  selector: 'app-game-community',
  imports: [IconComponent, RouterLink],
  templateUrl: './game-community.component.html',
  styleUrl: './game-community.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class GameCommunityComponent {
  content = input.required<GameCommunityContent>();
  gameName = input.required<string>();
  gameSlug = input.required<string>();
  accentColor = input.required<string>();

  private readonly eventsService = inject(EventsService);
  private readonly eventDialog = inject(EventDialogService);

  protected readonly copy = GAME_SECTION_COPY;

  /**
   * Próximos eventos de toda la tienda, de donde sale el relleno. Degradación
   * por consumidor, como en el resto del repo: si `/upcoming` cae, la lista
   * queda vacía y la landing sigue pintando sus eventos propios, que vienen en
   * el payload de la sección y no dependen de esta llamada.
   */
  private readonly upcomingPool = toSignal(
    this.eventsService.getUpcoming().pipe(
      catchError((err) => {
        console.error('[game-community] failed to load upcoming events', err);
        return of<StoreEvent[]>([]);
      }),
    ),
    { initialValue: [] as StoreEvent[] },
  );

  /**
   * Composición con prioridad, presentación cronológica (D8).
   *
   * La prioridad —propios → tienda → otros juegos— decide **qué entra** en los
   * 6; el conjunto resultante se **reordena por fecha** antes de pintarse. Un
   * evento de tienda del 12 va delante de uno propio del 30, porque es lo que
   * promete el rótulo «Próximos eventos». Que el evento propio quede empujado
   * hacia abajo es el precio asumido, no un fallo: ver los riesgos del spec.
   */
  protected readonly displayEvents = computed<CommunityEventView[]>(() => {
    const own = this.content().upcomingEvents;
    const seen = new Set(own.map((event) => event.id));
    const slug = this.gameSlug();

    const candidates = this.upcomingPool().filter((event) => !seen.has(event.id));

    // Un evento del pool que sea de ESTE juego no es relleno: no lleva chip ni
    // pierde el acento. Con el backend actual no debería llegar aquí (los
    // propios ya vienen en el payload de sección), pero clasificarlo mal
    // produciría justo el caso mixto que D3 y D9 prohíben.
    const ownFromPool = candidates.filter((event) => event.game?.slug === slug);
    const fromStore = candidates.filter((event) => !event.game);
    const fromOtherGames = candidates.filter(
      (event) => event.game && event.game.slug !== slug,
    );

    const composed: CommunityEventView[] = [
      ...own.map((event) => ({ event, isFiller: false })),
      ...ownFromPool.map((event) => ({ event, isFiller: false })),
      ...fromStore.map((event) => ({ event, isFiller: true })),
      ...fromOtherGames.map((event) => ({ event, isFiller: true })),
    ].slice(0, UPCOMING_EVENTS_LIMITS.gameSection);

    // `sort` es estable: a igualdad de fecha se respeta el orden de prioridad.
    return [...composed].sort((a, b) => startTime(a.event) - startTime(b.event));
  });

  protected readonly hasEvents = computed(() => this.displayEvents().length > 0);

  /**
   * Enlace bajo la zona de eventos. Con eventos propios lleva al listado del
   * juego, que es lo de siempre. Cuando la zona es **solo relleno**, ese
   * listado estaría vacío: en ese caso el enlace va a la agenda global, que es
   * donde el usuario sí va a encontrar lo que acaba de ver.
   */
  protected readonly eventsLink = computed(() =>
    this.content().upcomingEvents.length > 0
      ? ['/juegos', this.gameSlug(), 'eventos']
      : ['/eventos'],
  );

  protected readonly subtitle = computed(() =>
    interpolateGameCopy(GAME_SECTION_COPY.communitySubtitle, { game: this.gameName() }),
  );

  protected readonly calendarIcon: LucideIconData = Calendar;
  protected readonly trophyIcon: LucideIconData = Trophy;
  protected readonly messageIcon: LucideIconData = MessageCircle;
  protected readonly usersIcon: LucideIconData = Users;
  protected readonly mapPinIcon: LucideIconData = MapPin;
  protected readonly instagramIcon: LucideIconData = Instagram;
  // lucide no trae marca de TikTok: la nota musical es la convención aceptada.
  protected readonly tiktokIcon: LucideIconData = Music2;

  protected mascotError = signal(false);

  protected onMascotError(): void {
    this.mascotError.set(true);
  }

  protected platformIcon(platform: string): LucideIconData {
    switch (platform) {
      case 'whatsapp':
        return this.messageIcon;
      case 'discord':
        return this.usersIcon;
      case 'facebook':
        return this.usersIcon;
      case 'instagram':
        return this.instagramIcon;
      case 'tiktok':
        return this.tiktokIcon;
      default:
        return this.usersIcon;
    }
  }

  /**
   * El contrato emite `starts_at` en ISO 8601 y `price` numérico, no texto ya
   * maquetado: formatear es del front. Se delega en `event-format.util`, que
   * ancla la fecha a la zona de la tienda —con el `Intl` por defecto, un
   * usuario fuera de Costa Rica vería una hora que no es la del evento— y fija
   * el separador de miles a mano.
   */
  protected eventMeta(event: UpcomingEvent): string {
    return [formatEventRange(event.startsAt, event.endsAt), event.format]
      .filter(Boolean)
      .join(' · ');
  }

  protected eventPrice(event: UpcomingEvent): string {
    return formatEventPrice(event.price);
  }

  protected eventType(event: UpcomingEvent): string {
    return eventTypeLabel(event.type);
  }

  /**
   * Acento de la tarjeta. **Las dos señales van juntas**: acento de juego sin
   * chip = de aquí; acento neutro con chip = de fuera (D9).
   *
   * `--game-accent-color` no es decorativo —tiñe el borde izquierdo, el tipo,
   * el precio y el nombre—, así que un torneo de Yu-Gi-Oh en la landing de
   * Pokémon saldría con el color de Pokémon: el chip diría una cosa y el color
   * otra, que es peor que no poner chip. Las rellenadas usan el neutro de
   * marca y el chip hace todo el trabajo de identificar el juego.
   */
  protected accentFor(item: CommunityEventView): string {
    return item.isFiller ? 'var(--color-brand-primary)' : this.accentColor();
  }

  /** Rótulo del chip. Sin juego —o con uno despublicado (D12)— es de tienda. */
  protected chipLabel(event: UpcomingEvent): string {
    return event.game?.name ?? EVENTS_COPY.storeChip;
  }

  /** «Ver detalles de {nombre}»: mismo rótulo que `app-event-card`. */
  protected openLabel(event: UpcomingEvent): string {
    return `${EVENTS_COPY.openDetailsLabel} ${event.name}`;
  }

  /**
   * La tarjeta entera abre el detalle (D21). El enlace externo del evento ya no
   * vive aquí: está en el diálogo, como «Más información».
   */
  protected openDetails(event: UpcomingEvent): void {
    this.eventDialog.openEvent(event);
  }
}
